import DashboardLayout from "@/components/DashboardLayout";
import {
  SiteVisitOperationalList,
  type SiteVisitListFilter,
} from "@/components/site-visits/SiteVisitOperationalList";
import { trpc } from "@/lib/trpc";
import type { OperationalSiteVisitRow } from "@shared/siteVisitListBuckets";
import { SITE_VISIT_LIST_LIMITS } from "@shared/siteVisitListBuckets";
import { useEffect, useState } from "react";
import { useSearch } from "wouter";
import { usePathname, navigate } from "wouter/use-browser-location";

const NON_SCHEDULED_STATUSES = ["pending_review", "report_sent", "waived", "closed"];

function isNeedsMatching(visit: { status: string; visitType: string; createdBy?: string | null }) {
  const isOpen = !["closed", "report_sent", "waived"].includes(visit.status);
  return isOpen && visit.visitType === "urgent" && visit.createdBy === "Cleo";
}

export default function SiteVisits() {
  const searchStr = useSearch();
  const pathname = usePathname();
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState<SiteVisitListFilter>("today");
  const [pastLimit, setPastLimit] = useState<number>(SITE_VISIT_LIST_LIMITS.past);
  const searching = search.trim().length > 0;
  const operationalFilter = filterStatus === "today" || filterStatus === "upcoming" || filterStatus === "past";

  const utils = trpc.useUtils();
  useEffect(() => {
    const q = new URLSearchParams(searchStr).get("q");
    if (q?.trim()) setSearch(q.trim());
  }, [searchStr]);

  const legacy = trpc.siteVisits.list.useQuery({});
  const { data: pendingCount = 0 } = trpc.siteVisits.pendingReviewCount.useQuery();
  const operational = trpc.siteVisits.operationalList.useQuery({
    bucket: filterStatus === "upcoming" || filterStatus === "past" ? filterStatus : "today",
    search: searching ? search.trim() : undefined,
    limit: !searching && filterStatus === "past" ? pastLimit : undefined,
  });

  const legacyVisits = legacy.data ?? [];
  const needsMatchingCount = legacyVisits.filter(isNeedsMatching).length;
  const scheduledVisitsCount = legacyVisits.filter((visit) => visit.status === "scheduled").length;
  const legacyFiltered = legacyVisits.filter((visit) => {
    if (filterStatus === "reports") return NON_SCHEDULED_STATUSES.includes(visit.status);
    if (filterStatus === "needs_matching") return isNeedsMatching(visit);
    return true;
  }).map(toOperationalRow);

  const items = searching || operationalFilter ? (operational.data?.items ?? []) : legacyFiltered;
  const mode = searching ? "search" : filterStatus;
  const loading = searching || operationalFilter ? operational.isLoading : legacy.isLoading;
  const error = searching || operationalFilter ? operational.error?.message : legacy.error?.message;

  return (
    <DashboardLayout>
      <SiteVisitOperationalList
        filter={filterStatus}
        onFilter={(next) => {
          setFilterStatus(next);
          if (next !== "past") setPastLimit(SITE_VISIT_LIST_LIMITS.past);
        }}
        search={search}
        onSearch={setSearch}
        counts={{
          today: operational.data?.counts.today ?? 0,
          upcoming: operational.data?.counts.upcoming ?? 0,
          reports: pendingCount,
          needsMatching: needsMatchingCount,
        }}
        items={items}
        mode={mode}
        loading={loading}
        error={error}
        onRetry={() => {
          void utils.siteVisits.list.invalidate();
          void utils.siteVisits.operationalList.invalidate();
        }}
        onOpen={(id) => navigate(`/site-visits/id/${id}`, { state: { from: pathname } })}
        onCreate={() => navigate("/site-visits/new")}
        hasMore={operational.data?.hasMore}
        onLoadMore={() => setPastLimit((current) => Math.min(SITE_VISIT_LIST_LIMITS.pastMax, current + SITE_VISIT_LIST_LIMITS.past))}
        now={new Date()}
        scheduledVisitsCount={scheduledVisitsCount}
      />
    </DashboardLayout>
  );
}

function toOperationalRow(visit: {
  id: number;
  visitNumber: string;
  status: string;
  visitType: string;
  visitDate: Date | string;
  arrivalWindow?: string | null;
  technicianName?: string | null;
  picName?: string | null;
  billable?: boolean | null;
  waiverRequested?: boolean | null;
  waiverApproved?: boolean | null;
  followUpRequired?: boolean | null;
  followUpNotes?: string | null;
  custWhatsappStatus?: string | null;
  createdBy?: string | null;
  isCustomerUnmatched?: boolean | null;
  customerName?: string | null;
  customerAddress?: string | null;
  arrivedAt?: Date | string | null;
  departedAt?: Date | string | null;
}): OperationalSiteVisitRow {
  return {
    id: visit.id,
    visitNumber: visit.visitNumber,
    status: visit.status,
    visitType: visit.visitType,
    visitDate: visit.visitDate instanceof Date ? visit.visitDate.toISOString() : String(visit.visitDate),
    arrivalWindow: visit.arrivalWindow ?? null,
    technicianName: visit.technicianName ?? null,
    picName: visit.picName ?? null,
    billable: Boolean(visit.billable),
    waiverRequested: Boolean(visit.waiverRequested),
    waiverApproved: visit.waiverApproved ?? null,
    followUpRequired: Boolean(visit.followUpRequired),
    followUpNotes: visit.followUpNotes ?? null,
    custWhatsappStatus: visit.custWhatsappStatus ?? null,
    createdBy: visit.createdBy ?? null,
    isCustomerUnmatched: Boolean(visit.isCustomerUnmatched),
    customerName: visit.customerName ?? "Unknown",
    customerAddress: visit.customerAddress ?? null,
    arrivedAt: visit.arrivedAt == null ? null : visit.arrivedAt instanceof Date ? visit.arrivedAt.toISOString() : String(visit.arrivedAt),
    departedAt: visit.departedAt == null ? null : visit.departedAt instanceof Date ? visit.departedAt.toISOString() : String(visit.departedAt),
    cleoOnSite: false,
  };
}
